import React, { useContext } from 'react';
import { AppContext } from '../../App';
import { Calendar, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const PredictionsPanel = () => {
  const { dashboardData } = useContext(AppContext);
  
  if (!dashboardData) return null;
  
  const { predictions, trend_data } = dashboardData;

  // Combine historical + predictions for chart
  const last7Historical = trend_data.slice(-7).map(d => ({
    date: d.date.slice(5),
    inventory: d.inventory,
    type: 'actual'
  }));
  
  const predictionData = predictions.map(d => ({
    date: d.date.slice(5),
    inventory: d.predicted_inventory,
    confidence: d.confidence,
    type: 'predicted'
  }));

  const chartData = [...last7Historical, ...predictionData];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      return (
        <div className="glass p-3 rounded-lg">
          <p className="text-white text-sm font-medium mb-2">{label}</p>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between gap-4">
              <span className="text-[#A1A1AA]">Inventory:</span>
              <span className="text-white font-mono">{data?.inventory?.toLocaleString()}</span>
            </div>
            {data?.confidence && (
              <div className="flex justify-between gap-4">
                <span className="text-[#A1A1AA]">Confidence:</span>
                <span className="text-[#A855F7] font-mono">{(data.confidence * 100).toFixed(0)}%</span>
              </div>
            )}
            <div className="flex justify-between gap-4">
              <span className="text-[#A1A1AA]">Type:</span>
              <span className={data?.type === 'predicted' ? 'text-[#A855F7]' : 'text-[#3B82F6]'}>
                {data?.type === 'predicted' ? 'Forecast' : 'Actual'}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-card p-6" data-testid="predictions">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-[#A855F7]/10 rounded-lg">
          <Calendar className="w-5 h-5 text-[#A855F7]" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">7-Day Predictions</h3>
          <p className="text-sm text-[#A1A1AA]">AI-powered market forecasting</p>
        </div>
      </div>

      {/* Chart */}
      <div className="h-48 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <XAxis 
              dataKey="date" 
              stroke="#52525B" 
              tick={{ fill: '#A1A1AA', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              stroke="#52525B" 
              tick={{ fill: '#A1A1AA', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(val) => `${(val / 1000).toFixed(0)}K`}
              domain={['dataMin - 5000', 'dataMax + 5000']}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="inventory"
              stroke="#3B82F6"
              strokeWidth={2}
              dot={(props) => {
                const { cx, cy, payload } = props;
                if (payload.type === 'predicted') {
                  return (
                    <circle cx={cx} cy={cy} r={4} fill="#A855F7" stroke="#A855F7" strokeWidth={2} />
                  );
                }
                return null;
              }}
              strokeDasharray={(d) => d.type === 'predicted' ? '5 5' : '0'}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mb-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-6 h-0.5 bg-[#3B82F6]" />
          <span className="text-[#A1A1AA]">Historical</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-0.5 bg-[#A855F7]" style={{ backgroundImage: 'repeating-linear-gradient(90deg, #A855F7, #A855F7 3px, transparent 3px, transparent 6px)' }} />
          <span className="text-[#A1A1AA]">Predicted</span>
        </div>
      </div>

      {/* Predictions Table */}
      <div className="space-y-2">
        {predictions.slice(0, 4).map((pred, index) => (
          <div 
            key={index}
            className="flex items-center justify-between p-3 bg-[#27272A]/30 rounded-lg"
            data-testid={`prediction-${index}`}
          >
            <div className="flex items-center gap-3">
              <span className="text-white text-sm">{pred.date.slice(5)}</span>
              <div className="w-16 h-1.5 bg-[#27272A] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#A855F7] rounded-full"
                  style={{ width: `${pred.confidence * 100}%` }}
                />
              </div>
              <span className="text-xs text-[#A1A1AA]">{(pred.confidence * 100).toFixed(0)}%</span>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="text-right">
                <span className="text-[#52525B]">Inv: </span>
                <span className="text-white font-mono">{pred.predicted_inventory.toLocaleString()}</span>
              </div>
              <div className="text-right">
                <span className="text-[#52525B]">Price: </span>
                <span className="text-white font-mono">${pred.predicted_avg_price.toLocaleString()}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PredictionsPanel;
